import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Trash2, X, ExternalLink, CheckCircle2, XCircle,
  FileText, FileWarning, Loader2, Pencil, Sparkles, Megaphone, Bell,
} from "lucide-react";
import adminApi, { STATIC_ORIGIN } from "./adminApi";

const emptyNotice = { tender_name: "", tender_details: "", check_new: false, check_mark: false, check_notif: false };
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png";
const ALLOWED_EXT = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".jpg", ".jpeg", ".png"];

function attachmentUrl(filename) {
  if (!filename) return null;
  const fileName = filename.split(/[\\/]/).pop();
  return STATIC_ORIGIN + "/images/downloadnotices/" + fileName;
}

export default function ManageDownloadNotices() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = "add new"
  const [notice, setNotice] = useState(emptyNotice);
  const [file, setFile] = useState(null);
  const [filePreviewName, setFilePreviewName] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [noticeError, setNoticeError] = useState("");
  const fileInputRef = useRef(null);

  // for pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

  const showToast = (type, message) => {
    setToast({ type: type, message: message });
    setTimeout(function () {
      setToast(null);
    }, 2600);
  };

  const fetchNotices = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/download-notices", {
        params: { page: page, limit: limit },
      });

      setNotices(res.data.downloadNotices || []);
      setTotalPages(res.data.pagination.totalPages);
      setTotalRecords(res.data.pagination.total);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Could not load notices.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(function () {
    fetchNotices();
  }, [page]);

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    if (selected.length === notices.length) {
      setSelected([]);
    } else {
      setSelected(notices.map(function (t) { return t.tender_id; }));
    }
  };
  const toggleSelectOne = (id) => {
    setSelected(function (prev) {
      if (prev.includes(id)) {
        return prev.filter(function (x) { return x !== id; });
      }
      return prev.concat([id]);
    });
  };

  // ── Status toggle ────────────────────────────────────────────────────────
  const handleToggleStatus = async (item, e) => {
    e.stopPropagation();
    const turningOn = item.status === 0;
    const label = turningOn ? "Active" : "Inactive";
    const ok = window.confirm("Are you sure you want to change the status to " + label + "?");
    if (!ok) return;

    try {
      const res = await adminApi.patch("/admin/download-notices/" + item.tender_id + "/status");
      setNotices(function (prev) {
        return prev.map(function (t) {
          if (t.tender_id === item.tender_id) {
            return Object.assign({}, t, { status: res.data.status });
          }
          return t;
        });
      });
    } catch (err) {
      showToast("error", (err.response && err.response.data && err.response.data.message) || "Could not update status.");
    }
  };

  // ── Bulk delete ──────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(
      "Are you sure you want to delete " + selected.length + " selected notice(s)? This cannot be undone."
    );
    if (!ok) return;

    setBulkDeleting(true);
    try {
      await adminApi.post("/admin/download-notices/bulk-delete", { ids: selected });
      showToast("success", selected.length + " notice(s) deleted.");
      setSelected([]);
      fetchNotices();
    } catch (err) {
      showToast("error", (err.response && err.response.data && err.response.data.message) || "Could not delete selected notices.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Add / Edit modal ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setNotice(emptyNotice);
    setFile(null);
    setFilePreviewName(null);
    setExistingAttachment(null);
    setNoticeError("");
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingId(item.tender_id);
    setNotice({
      tender_name: item.tender_name,
      tender_details: item.tender_details || "",
      check_new: !!item.check_new,
      check_mark: !!item.check_mark,
      check_notif: !!item.check_notif,
    });
    setFile(null);
    setFilePreviewName(null);
    setExistingAttachment(item.attachment);
    setNoticeError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
    if (ALLOWED_EXT.indexOf(ext) === -1) {
      setNoticeError("Only PDF, Word, Excel, JPG, JPEG and PNG files are allowed.");
      return;
    }
    setNoticeError("");
    setFile(f);
    setFilePreviewName(f.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNoticeError("");

    if (!notice.tender_name.trim()) {
      setNoticeError("Notice Name is required.");
      return;
    }
    if (!notice.tender_details.trim()) {
      setNoticeError("Brief Details is required.");
      return;
    }
    if (!editingId && !file) {
      setNoticeError("File attachment is required.");
      return;
    }

    const fd = new FormData();
    fd.append("tender_name", notice.tender_name.trim());
    fd.append("tender_details", notice.tender_details.trim());
    fd.append("check_new", notice.check_new ? "1" : "0");
    fd.append("check_mark", notice.check_mark ? "1" : "0");
    fd.append("check_notif", notice.check_notif ? "1" : "0");
    if (file) fd.append("attachment", file);

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put("/admin/download-notices/" + editingId, fd);
        showToast("success", "notice updated.");
      } else {
        await adminApi.post("/admin/download-notices", fd);
        showToast("success", "notice added.");
      }
      setShowModal(false);
      fetchNotices();
    } catch (err) {
      setNoticeError((err.response && err.response.data && err.response.data.message) || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = notices.length > 0 && selected.length === notices.length;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar — stays fixed, never scrolls */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Notice Board
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {selected.length > 0 ? (
              <motion.button
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 text-sm font-medium px-3.5 py-2 rounded-lg transition disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Delete Selected ({selected.length})
              </motion.button>
            ) : null}
          </AnimatePresence>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Plus size={16} />
            Add New Notice
          </button>
        </div>
      </div>

      {/* Table card — this is the only part that scrolls, with a sticky header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading download notices...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : notices.length === 0 ? (
          <div className="py-16 text-center">
            <FileText size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No notices yet. Click Add New Notice to create one.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-base">
                  <th className="th-colorful pl-3 pr-2 py-3 w-8 border-b-2 border-r normal-case">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                    />
                  </th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r" style={{ width: "40%" }}>
                    Notice Name
                  </th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Notice Details</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r" style={{ width: "2%" }}>
                    Attachments
                  </th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide text-center border-b-2">Status</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {notices.map(function (t, i) {
                    return (
                      <motion.tr
                        key={t.tender_id}
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, delay: i * 0.02 }}
                        onClick={function () { openEditModal(t); }}
                      >
                        <td
                          className="pl-3 pr-2 py-2 border-r border-slate-200"
                          onClick={function (e) { e.stopPropagation(); }}
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(t.tender_id)}
                            onChange={function () { toggleSelectOne(t.tender_id); }}
                            className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-2 text-slate-800 font-medium border-r border-slate-200">
                          <div className="flex items-center gap-2">
                            {t.tender_name}
                            {t.check_new ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                                <Sparkles size={10} /> New
                              </span>
                            ) : null}
                            {t.check_mark ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-sky-600 bg-sky-50 px-1.5 py-0.5 rounded-full">
                                <Megaphone size={10} /> Marquee
                              </span>
                            ) : null}
                            {t.check_notif ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-full">
                                <Bell size={10} /> Notify
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td
                          className="px-3 py-2 text-slate-500 max-w-[280px] truncate border-r border-slate-200"
                          title={t.tender_details}
                        >
                          {t.tender_details || "-"}
                        </td>
                        <td className="px-3 py-2 border-r border-slate-200" style={{ textAlign: "center" }}>
                          {t.attachment ? (
                            <a
                              href={attachmentUrl(t.attachment)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={function (e) { e.stopPropagation(); }}
                              className="inline-flex items-center gap-1.5 text-[#0EA5A4] hover:underline"
                            >
                              <div
                                style={{
                                  border: "0.5px solid #999",
                                  padding: "7px",
                                  marginTop: "3px",
                                  marginBottom: "3px",
                                  borderRadius: "7px",
                                }}
                              >
                                <i style={{ color: "green" }} className="fa fa-eye" aria-hidden="true" />
                              </div>
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-slate-300 text-xs">
                              <FileWarning size={14} /> None
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 border-r border-slate-200">
                          <div className="flex items-center justify-center">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={function (e) { handleToggleStatus(t, e); }}
                              title={t.status ? "Active - click to deactivate" : "Inactive - click to activate"}
                              className={
                                "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition " +
                                (t.status
                                  ? "bg-[#0EA5A4]/10 text-[#0B7E7D] hover:bg-[#0EA5A4]/20"
                                  : "bg-slate-100 text-slate-400 hover:bg-slate-200")
                              }
                            >
                              {t.status ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                              {t.status ? "Active" : "Inactive"}
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-2.5">
          <div className="text-sm text-slate-500">Total Records: {totalRecords}</div>

          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={function () { setPage(page - 1); }}
              className="px-2.5 py-1 text-sm border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>

            <span className="text-sm font-medium">
              Page {page} of {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={function () { setPage(page + 1); }}
              className="px-3 py-1 border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={
              "fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium " +
              (toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white")
            }
          >
            {toast.message}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {showModal ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={function (e) { e.stopPropagation(); }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  {editingId ? "Edit Notice" : "Add New Notice"}
                </h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Notice Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={notice.tender_name}
                    onChange={function (e) { setNotice(Object.assign({}, notice, { tender_name: e.target.value })); }}
                    placeholder="e.g. Application for Hostel Admission"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Brief Details <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={notice.tender_details}
                    onChange={function (e) { setNotice(Object.assign({}, notice, { tender_details: e.target.value })); }}
                    placeholder="Short description of the notice..."
                    rows={3}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    File <span className="text-red-500">*</span>{" "}
                    <span className="text-red-500">(PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG)</span>
                  </label>
                  <div
                    onClick={function () { fileInputRef.current && fileInputRef.current.click(); }}
                    className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition"
                  >
                    <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                      <FileText size={18} className="text-slate-300" />
                    </div>
                    <div className="text-xs text-slate-500">
                      {filePreviewName
                        ? filePreviewName
                        : existingAttachment
                        ? "Click to replace attachment"
                        : "Click to choose a file"}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPT}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={notice.check_new}
                    onChange={function (e) { setNotice(Object.assign({}, notice, { check_new: e.target.checked })); }}
                    className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                  />
                  <span className="text-sm text-slate-600">Mark as new</span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={notice.check_mark}
                    onChange={function (e) { setNotice(Object.assign({}, notice, { check_mark: e.target.checked })); }}
                    className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                  />
                  <span className="text-sm text-slate-600">View in Marquee</span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={notice.check_notif}
                    onChange={function (e) { setNotice(Object.assign({}, notice, { check_notif: e.target.checked })); }}
                    className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                  />
                  <span className="text-sm text-slate-600">Send Notification</span>
                </label>

                {noticeError ? (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {noticeError}
                  </p>
                ) : null}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : editingId ? (
                      <Pencil size={15} />
                    ) : (
                      <Plus size={15} />
                    )}
                    {saving ? "Saving..." : editingId ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
